export interface AppRoutesType {
  '403': string
  '404': string
  terms: string
  privacy: string
  licenses: string
  imprint: string
  cookie: string
  _offline: string
  index: string
  home: string
  userSettings: string
  wc: string
  stake: string
  swap: string
  bridge: string
  earn: string
  addOwner: string
  addressBook: string
  apps: {
    open: string
    index: string
    custom: string
    bookmarked: string
  }
  balances: {
    nfts: string
    index: string
  }
  newSafe: {
    load: string
    create: string
    advancedCreate: string
  }
  settings: {
    setup: string
    security: string
    notifications: string
    modules: string
    index: string
    environmentVariables: string
    data: string
    cookies: string
    appearance: string
    safeApps: {
      index: string
    }
  }
  share: {
    safeApp: string
  }
  spaces: {
    settings: string
    safeAccounts: string
    members: string
    index: string
  }
  transactions: {
    tx: string
    queue: string
    msg: string
    messages: string
    index: string
    history: string
  }
  welcome: {
    spaces: string
    index: string
    accounts: string
  }
}

const baseRoutes: AppRoutesType = {
  // common routes
  '403': '/403',
  '404': '/404',
  terms: '/terms',
  privacy: '/privacy',
  licenses: '/licenses',
  imprint: '/imprint',
  cookie: '/cookie',
  _offline: '/_offline',
  index: '/',

  // dynamic routes
  home: '/home',
  userSettings: '/user-settings',
  wc: '/wc',
  stake: '/stake',
  swap: '/swap',
  bridge: '/bridge',
  earn: '/earn',
  addOwner: '/addOwner',
  addressBook: '/address-book',
  apps: {
    open: '/apps/open',
    index: '/apps',
    custom: '/apps/custom',
    bookmarked: '/apps/bookmarked',
  },
  balances: {
    nfts: '/balances/nfts',
    index: '/balances',
  },
  newSafe: {
    load: '/new-safe/load',
    create: '/new-safe/create',
    advancedCreate: '/new-safe/advanced-create',
  },
  settings: {
    setup: '/settings/setup',
    security: '/settings/security',
    notifications: '/settings/notifications',
    modules: '/settings/modules',
    index: '/settings',
    environmentVariables: '/settings/environment-variables',
    data: '/settings/data',
    cookies: '/settings/cookies',
    appearance: '/settings/appearance',
    safeApps: {
      index: '/settings/safe-apps',
    },
  },
  share: {
    safeApp: '/share/safe-app',
  },
  spaces: {
    settings: '/spaces/settings',
    safeAccounts: '/spaces/safe-accounts',
    members: '/spaces/members',
    index: '/spaces',
  },
  transactions: {
    tx: '/transactions/tx',
    queue: '/transactions/queue',
    msg: '/transactions/msg',
    messages: '/transactions/messages',
    index: '/transactions',
    history: '/transactions/history',
  },
  welcome: {
    spaces: '/welcome/spaces',
    index: '/welcome',
    accounts: '/welcome/accounts',
  },
}

// get the prefix based on the environment
const getEnvironmentPrefix = (environment: 'evm' | 'hedera'): string => {
  return environment === 'hedera' ? '/hedera' : '/evm'
}

// applies the prefix to a given route if needed
const applyPrefix = (route: string, prefix: string): string => {
  if (
    route.startsWith('/') &&
    !route.startsWith('/home') &&
    !route.startsWith('/apps') &&
    !route.startsWith('/balances') &&
    !route.startsWith('/new-safe') &&
    !route.startsWith('/settings') &&
    !route.startsWith('/share') &&
    !route.startsWith('/spaces') &&
    !route.startsWith('/transactions') &&
    !route.startsWith('/welcome') &&
    !route.startsWith('/user-settings') &&
    !route.startsWith('/wc') &&
    !route.startsWith('/stake') &&
    !route.startsWith('/swap') &&
    !route.startsWith('/bridge') &&
    !route.startsWith('/earn') &&
    !route.startsWith('/addOwner') &&
    !route.startsWith('/address-book')
  ) {
    return route
  }
  return prefix + route
}

const processRoutes = (routes: any, prefix: string): any => {
  if (typeof routes === 'string') {
    return applyPrefix(routes, prefix)
  }

  if (Array.isArray(routes)) {
    return routes.map((route) => processRoutes(route, prefix))
  }

  if (routes && typeof routes === 'object') {
    const result: any = {}
    for (const [key, value] of Object.entries(routes)) {
      result[key] = processRoutes(value, prefix)
    }
    return result
  }

  return routes
}

class RouteManager {
  private currentEnvironment: 'evm' | 'hedera' = 'evm'

  setEnvironment(environment: 'evm' | 'hedera') {
    this.currentEnvironment = environment
  }

  getEnvironment(): 'evm' | 'hedera' {
    return this.currentEnvironment
  }

  private getRoutesWithPrefix(environment: 'evm' | 'hedera') {
    const prefix = getEnvironmentPrefix(environment)
    return processRoutes(baseRoutes, prefix)
  }

  get routes() {
    return this.getRoutesWithPrefix(this.currentEnvironment)
  }

  getRoutesForEnvironment(environment: 'evm' | 'hedera') {
    return this.getRoutesWithPrefix(environment)
  }

  getRoute(path: string, environment?: 'evm' | 'hedera') {
    const env = environment || this.currentEnvironment
    const prefix = getEnvironmentPrefix(env)
    return applyPrefix(path, prefix)
  }
}

const routeManager = new RouteManager()

const createRoutesProxy = <T extends Record<string, any>>(routes: T): T => {
  return new Proxy(routes, {
    get(target, prop) {
      const value = target[prop as keyof T]

      if (value && typeof value === 'object' && !Array.isArray(value)) {
        return createRoutesProxy(value)
      }

      return value
    },
  }) as T
}

const updateAppRoutes = (environment: 'evm' | 'hedera') => {
  const newRoutes = routeManager.getRoutesForEnvironment(environment)

  Object.keys(newRoutes).forEach((key) => {
    const typedKey = key as keyof AppRoutesType
    ;(AppRoutes as any)[typedKey] = newRoutes[typedKey]
  })
}

export const getAppRoutes = (environment: 'evm' | 'hedera'): AppRoutesType => {
  return routeManager.getRoutesForEnvironment(environment)
}

const defaultRoutes = routeManager.getRoutesForEnvironment('evm')

export const AppRoutes: AppRoutesType = createRoutesProxy(defaultRoutes)

export const setAppRoutesEnvironment = (environment: 'evm' | 'hedera') => {
  routeManager.setEnvironment(environment)
  updateAppRoutes(environment)
}

export { routeManager }
